import React, { useState, useEffect, useRef } from 'react';
import { Table, Select, Input, Form, Row, Col, Tag, Modal, DatePicker } from 'antd';
import { parseISO, format } from 'date-fns';
import { debounce } from 'lodash';
import Rest from 'tools/rest';
import Preloader from 'components/preloader';
import RequestCard from 'components/request_card';
import {
  normalizeApproved,
  formatMaterialIntegerDisplay,
  materialDisplayUnit,
} from '../utils';

const { RangePicker } = DatePicker;

const APPROVED_OPTIONS = [
  { value: 'pending', label: 'Ожидает' },
  { value: 'approved', label: 'Подтверждён' },
  { value: 'rejected', label: 'Отклонён' },
];

const APPROVED_COLORS = {
  pending: 'orange',
  approved: 'green',
  rejected: 'red',
};

const SOURCE_TYPE_LABELS = {
  Invoice: 'Накладная',
  WarehouseMaterial: 'Склад',
  Inventory: 'Инвентарь',
};

const DEST_TYPE_LABELS = {
  WarehouseMaterial: 'Склад',
  Inventory: 'Исполнитель',
  Request: 'Задача',
};

const MovesGrid = ({ dataVersion, materialId, requestId, compact, onDataChange }) => {
  const [moves, setMoves] = useState([]);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState({ current: 1, pageSize: 10, total: 0 });
  const [sorter, setSorter] = useState({ order: 'desc', order_by: 'created_at' });
  const [search, setSearch] = useState({});
  const [requestModal, setRequestModal] = useState(null);

  const debounceRef = useRef(null);

  useEffect(() => {
    loadMoves();
  }, [meta.current, meta.pageSize, sorter, search.approved, search.source_type,
      search.destination_type, search.from, search.to, dataVersion]);

  useEffect(() => {
    if (debounceRef.current) debounceRef.current.cancel();
    debounceRef.current = debounce(loadMoves, 500);
    debounceRef.current();
    return () => debounceRef.current?.cancel();
  }, [search.material_name]);

  const loadMoves = () => {
    const params = {
      page: meta.current,
      per: meta.pageSize,
      order: sorter.order,
      order_by: sorter.order_by,
      search: { ...search },
    };
    if (materialId) params.search.material_id = materialId;
    if (requestId) params.search.destination_id = requestId;

    setLoading(true);
    Rest.get('/api/v1/material_moves.json', { params })
      .then((r) => {
        setMoves(r.data.material_moves || []);
        setMeta((prev) => ({
          ...prev,
          total: r.data.meta?.total || 0,
          current: r.data.meta?.page || prev.current,
        }));
      })
      .finally(() => setLoading(false));
  };

  const handleTableChange = (pagination, _filters, sort) => {
    setMeta((prev) => ({ ...prev, current: pagination.current, pageSize: pagination.pageSize }));
    if (sort.column) {
      setSorter({
        order: sort.order === 'ascend' ? 'asc' : 'desc',
        order_by: sort.field,
      });
    }
  };

  const handleDateRange = (dates) => {
    if (dates) {
      setSearch((s) => ({
        ...s,
        from: dates[0].toISOString(),
        to: dates[1].toISOString(),
      }));
    } else {
      setSearch((s) => ({ ...s, from: undefined, to: undefined }));
    }
  };

  const moveTypeTag = (move) => {
    if (move.source_type === 'Invoice') return <Tag color="green">Приход</Tag>;
    return <Tag color="volcano">Расход</Tag>;
  };

  const columns = [
    {
      title: 'Тип', width: '8%',
      render: (_, record) => moveTypeTag(record),
    },
    { title: 'Материал', dataIndex: 'material_name', sorter: true, width: '18%' },
    {
      title: 'Кол-во',
      dataIndex: 'quantity',
      width: '8%',
      render: (qty) => formatMaterialIntegerDisplay(qty),
    },
    {
      title: 'Ед. изм.',
      dataIndex: 'material_unit',
      width: '7%',
      render: (_, record) => materialDisplayUnit(record) || '—',
    },
    { title: 'Откуда', dataIndex: 'source_label', width: '13%' },
    {
      title: 'Файл накладной',
      width: '12%',
      ellipsis: true,
      render: (_, record) => {
        if (record.source_type !== 'Invoice') return '—';
        if (record.invoice_document_url) {
          return (
            <a
              href={record.invoice_document_url}
              target="_blank"
              rel="noopener noreferrer"
              title={record.invoice_document_filename || ''}
            >
              {record.invoice_document_filename || 'Открыть'}
            </a>
          );
        }
        return '—';
      },
    },
    { title: 'Куда', dataIndex: 'destination_label', width: '14%',
      render: (text, record) => {
        if (record.destination_type === 'Request') {
          return (
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); setRequestModal(record.destination_id); }}
            >
              {text}
            </a>
          );
        }
        return text;
      },
    },
    {
      title: 'Статус', dataIndex: 'approved', width: '10%',
      render: (status) => {
        const key = normalizeApproved(status);
        return (
          <Tag color={APPROVED_COLORS[key] || 'default'}>
            {APPROVED_OPTIONS.find((o) => o.value === key)?.label || String(status)}
          </Tag>
        );
      },
    },
    {
      title: 'Дата',
      width: '15%',
      render: (_, record) => {
        const key = normalizeApproved(record.approved);
        const raw =
          key === 'pending'
            ? record.created_at
            : (record.updated_at || record.created_at);
        return raw ? format(parseISO(raw), 'dd.MM.yyyy HH:mm') : '—';
      },
    },
  ];

  if (compact) {
    return (
      <Table
        loading={loading}
        columns={columns}
        dataSource={moves}
        rowKey="id"
        size="small"
        pagination={{ ...meta, showSizeChanger: true }}
        onChange={handleTableChange}
      />
    );
  }

  return (
    <Preloader loading={loading}>
      <Row style={{ marginBottom: 16 }}>
        <Col>
          <Form layout="inline">
            <Form.Item>
              <Select
                value={search.source_type}
                onChange={(v) => setSearch((s) => ({ ...s, source_type: v }))}
                placeholder="Откуда"
                style={{ width: 140 }}
                allowClear
                options={Object.entries(SOURCE_TYPE_LABELS).map(([k, v]) => ({ value: k, label: v }))}
              />
            </Form.Item>
            <Form.Item>
              <Select
                value={search.destination_type}
                onChange={(v) => setSearch((s) => ({ ...s, destination_type: v }))}
                placeholder="Куда"
                style={{ width: 140 }}
                allowClear
                options={Object.entries(DEST_TYPE_LABELS).map(([k, v]) => ({ value: k, label: v }))}
              />
            </Form.Item>
            <Form.Item>
              <Select
                value={search.approved}
                onChange={(v) => setSearch((s) => ({ ...s, approved: v }))}
                placeholder="Статус"
                style={{ width: 140 }}
                allowClear
                options={APPROVED_OPTIONS}
              />
            </Form.Item>
            <Form.Item>
              <RangePicker
                showTime={{ format: 'HH:mm' }}
                onChange={handleDateRange}
              />
            </Form.Item>
          </Form>
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={moves}
        rowKey="id"
        size="small"
        pagination={{ ...meta, showSizeChanger: true }}
        onChange={handleTableChange}
      />

      {requestModal && (
        <Modal
          title={`Задача #${requestModal}`}
          open={!!requestModal}
          onCancel={() => {
            setRequestModal(null);
            onDataChange?.();
          }}
          footer={null}
          width="95%"
        >
          <RequestCard
            request_id={requestModal}
            onMaterialsChange={onDataChange}
          />
        </Modal>
      )}
    </Preloader>
  );
};

export default MovesGrid;
